/**
 * Vehicle-registration modal — used by the SMS-only login flow when a user
 * tries to search before having registered a vehicle. The modal collects:
 *   - מספר רכב   (live-formatted, 7-8 digits)
 *   - שנת ייצור  (chip grid, current year → 1980)
 *   - חודש בעלות (YYYY-MM, matched against gov registry)
 *
 * On submit it calls `vehicle.service.register()` which performs the same
 * Phase-1/2/3 atomic-batch dance as `replace()`. On success the modal closes
 * and the page reloads-in-place via `window.showHomeScreen()` so the rest of
 * the app picks up `LocalState.plate`.
 *
 * The DOM is rendered into a single overlay container (`#registerVehicleOverlay`)
 * declared in index.html. We render the wizard via innerHTML on `open()` and
 * tear it down on `close()` — keeps the markup self-contained.
 */

import * as alert from '../../core/alert.js';
import * as spinner from '../../core/spinner.js';
import { register as registerVehicle } from './vehicle.service.js';
import { format as formatPlate } from '../../shared/plate.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('register.view');

const OVERLAY_ID = 'registerVehicleOverlay';
const MIN_YEAR = 1980;

/** @type {((value: { status: 'registered' | 'cancelled' }) => void) | null} */
let pendingResolve = null;

/**
 * Open the modal. Returns a promise that resolves when it closes — with
 * `{ status: 'registered' }` on success or `{ status: 'cancelled' }`
 * otherwise. Callers can `await open()` and inspect the result to decide
 * what to do next (e.g. resume a gated search).
 *
 * @returns {Promise<{ status: 'registered' | 'cancelled' }>}
 */
export function open() {
  return new Promise((resolve) => {
    const overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) {
      log.error('registerVehicleOverlay element missing');
      resolve({ status: 'cancelled' });
      return;
    }
    pendingResolve = resolve;
    overlay.innerHTML = render();
    overlay.style.display = 'flex';
    overlay.classList.add('open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    wire(overlay);
    // Focus the plate input.
    setTimeout(() => {
      /** @type {HTMLInputElement | null} */
      (overlay.querySelector('#rvPlate'))?.focus();
    }, 80);
  });
}

/** @param {'registered' | 'cancelled'} status */
function close(status) {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    overlay.classList.remove('open');
    overlay.style.display = 'none';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = '';
  }
  document.body.classList.remove('no-scroll');
  const r = pendingResolve;
  pendingResolve = null;
  if (r) r({ status });
}

function render() {
  return `
    <div class="rv-card" role="dialog" aria-modal="true" aria-labelledby="rvTitle">
      <button type="button" class="rv-close" data-action="close" aria-label="סגור">×</button>
      <h2 id="rvTitle" class="rv-title">רישום הרכב שלך 🚗</h2>
      <p class="rv-sub">פרטי הרכב נשמרים לחשבונך — לאחר הרישום תוכל לחפש רכבים אחרים.</p>

      <label class="rv-field-label" for="rvPlate">מספר רכב</label>
      <div class="rv-plate-wrap">
        <input type="text" id="rvPlate" inputmode="numeric" maxlength="10"
               placeholder="00-000-000" class="rv-input rv-plate-input"
               autocomplete="off" autocapitalize="off" autocorrect="off" />
      </div>

      <label class="rv-field-label">שנת ייצור</label>
      <div class="rv-year-grid" id="rvYearGrid"></div>
      <input type="hidden" id="rvYear" />

      <label class="rv-field-label" for="rvOwnership">חודש בעלות (YYYY-MM)</label>
      <input type="month" id="rvOwnership" class="rv-input" placeholder="YYYY-MM" />
      <p class="rv-hint">החודש שבו רכשת את הרכב (לפי משרד התחבורה).</p>

      <div class="rv-actions">
        <button type="button" class="rv-btn rv-btn-ghost" data-action="close">ביטול</button>
        <button type="button" class="rv-btn rv-btn-primary" id="rvSubmit">רשום רכב</button>
      </div>

      <div id="rvAlert" class="rv-alert" role="alert" hidden></div>
    </div>
  `;
}

/** @param {HTMLElement} overlay */
function wire(overlay) {
  // Close handlers
  overlay.querySelectorAll('[data-action="close"]').forEach((el) => {
    el.addEventListener('click', () => close('cancelled'));
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close('cancelled');
  });

  // Plate auto-format
  const plateInput = /** @type {HTMLInputElement | null} */ (overlay.querySelector('#rvPlate'));
  plateInput?.addEventListener('input', () => {
    const digits = (plateInput.value || '').replace(/\D/g, '').slice(0, 8);
    plateInput.value = formatPlate(digits);
    plateInput.dataset.digits = digits;
  });

  // Year chip grid
  const grid = /** @type {HTMLElement | null} */ (overlay.querySelector('#rvYearGrid'));
  const yearInput = /** @type {HTMLInputElement | null} */ (overlay.querySelector('#rvYear'));
  if (grid && yearInput) {
    const current = new Date().getFullYear();
    const frag = document.createDocumentFragment();
    for (let y = current; y >= MIN_YEAR; y--) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'rv-year-chip';
      b.textContent = String(y);
      b.dataset.year = String(y);
      frag.appendChild(b);
    }
    grid.appendChild(frag);
    grid.addEventListener('click', (e) => {
      const chip = /** @type {HTMLElement} */ (e.target).closest?.('.rv-year-chip');
      if (!chip) return;
      grid.querySelectorAll('.rv-year-chip').forEach((c) => c.classList.toggle('active', c === chip));
      yearInput.value = /** @type {HTMLElement} */ (chip).dataset.year || '';
    });
  }

  // Submit
  const submit = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('#rvSubmit'));
  submit?.addEventListener('click', () => { void onSubmit(overlay); });
}

/** @param {HTMLElement} overlay */
async function onSubmit(overlay) {
  const plateInput  = /** @type {HTMLInputElement | null} */ (overlay.querySelector('#rvPlate'));
  const yearInput   = /** @type {HTMLInputElement | null} */ (overlay.querySelector('#rvYear'));
  const ownerInput  = /** @type {HTMLInputElement | null} */ (overlay.querySelector('#rvOwnership'));
  const alertEl     = /** @type {HTMLElement | null} */ (overlay.querySelector('#rvAlert'));
  const submitBtn   = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('#rvSubmit'));

  const plate = String(plateInput?.dataset.digits || plateInput?.value || '').replace(/\D/g, '');
  const year  = String(yearInput?.value || '').replace(/\D/g, '');
  const ownership = String(ownerInput?.value || '').trim();

  if (alertEl) { alertEl.hidden = true; alertEl.textContent = ''; }

  if (plate.length < 7) { showErr(alertEl, 'מספר רכב לא תקין — נדרשות 7 או 8 ספרות.'); return; }
  if (!/^\d{4}$/.test(year)) { showErr(alertEl, 'בחר שנת ייצור.'); return; }

  if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = '⏳ רושם...'; }
  const release = spinner.show('vehicle.register', 'רושם רכב... אנא המתן');

  try {
    // Orphan code path — the active flow goes through replaceVehicle.view.js
    // (unified register+replace modal). Kept for back-compat; production-year
    // is now the verification factor (no more ownership month).
    const result = await registerVehicle({ plate, year });
    if (result.status === 'ok') {
      alert.showToast('הרכב נרשם בהצלחה — אפשר לחפש עכשיו.', { kind: 'success', duration: 3000 });
      close('registered');
      return;
    }
    if (result.status === 'mismatch') {
      showErr(
        alertEl,
        result.message || 'חודש הבעלות אינו תואם לרישומי משרד התחבורה.',
      );
      return;
    }
    if (result.status === 'taken') {
      showErr(alertEl, result.message || 'הרכב משויך כבר למשתמש אחר.');
      return;
    }
    showErr(alertEl, result.message || 'שגיאה ברישום הרכב.');
  } catch (err) {
    log.error('register threw:', err);
    showErr(alertEl, 'שגיאה לא צפויה. נסה שוב.');
  } finally {
    release();
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'רשום רכב'; }
  }
}

/** @param {HTMLElement | null} el @param {string} msg */
function showErr(el, msg) {
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
}
